import { useState } from 'react';
import { X, User, Server, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import type { Registration } from '../../types/registration';

interface RegistrationDetailModalProps {
  registration: Registration;
  onClose: () => void;
  onDelete: () => void;
}

export function RegistrationDetailModal({
  registration,
  onClose,
  onDelete,
}: RegistrationDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'basic' | 'technical'>('basic');
  const [technicalExpanded, setTechnicalExpanded] = useState(true);

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString();
  };

  const getTransportBadgeColor = (transport: string): string => {
    switch (transport.toLowerCase()) {
      case 'tls': return 'bg-green-100 text-green-800 border-green-200';
      case 'tcp': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'udp': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (isActive: boolean): string => {
    return isActive 
      ? 'bg-green-100 text-green-800 border-green-200' 
      : 'bg-red-100 text-red-800 border-red-200';
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${registration.isActive ? 'bg-green-100' : 'bg-red-100'}`}>
                <User className={`h-5 w-5 ${registration.isActive ? 'text-green-600' : 'text-red-600'}`} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Registration Details</h2>
                <p className="text-sm text-gray-500 font-mono">{registration.registrationId}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Status Banner */}
          <div className={`px-6 py-3 ${registration.isActive ? 'bg-green-50' : 'bg-red-50'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className={`h-2 w-2 rounded-full ${registration.isActive ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className={`font-medium ${registration.isActive ? 'text-green-800' : 'text-red-800'}`}>
                  {registration.isActive ? 'Active Registration' : 'Expired Registration'}
                </span>
                {registration.authenticated && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-200 text-green-800">
                    <Shield className="h-3 w-3 mr-1" />
                    Authenticated
                  </span>
                )}
              </div>
              {registration.isActive && (
                <span className="text-sm text-green-700">
                  Expires in {formatDuration(registration.expiresIn)}
                </span>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('basic')}
                className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'basic'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <User className="h-4 w-4 inline mr-2" />
                Basic Info
              </button>
              <button
                onClick={() => setActiveTab('technical')}
                className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'technical'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Server className="h-4 w-4 inline mr-2" />
                Technical Details
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto max-h-[50vh]">
            {activeTab === 'basic' && (
              <div className="space-y-6">
                {/* Identity */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Identity</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Username</span>
                      <span className="text-sm font-medium text-gray-900">{registration.username}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Domain</span>
                      <span className="text-sm font-medium text-gray-900">{registration.domain}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Address of Record (AOR)</span>
                      <span className="text-sm font-mono text-gray-900">{registration.aor}</span>
                    </div>
                  </div>
                </div>

                {/* Contact */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Contact Information</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <span className="text-sm text-gray-600 block mb-1">Contact URI</span>
                      <span className="text-sm font-mono text-gray-900 break-all">{registration.contact}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Source Address</span>
                      <span className="text-sm font-mono text-gray-900">
                        {registration.sourceIp}:{registration.sourcePort}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Transport</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTransportBadgeColor(registration.transport)}`}>
                        {registration.transport.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Timing */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Timing</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Status</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(registration.isActive)}`}>
                        {registration.isActive ? 'Active' : 'Expired'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Registered At</span>
                      <span className="text-sm text-gray-900">{formatTimestamp(registration.registeredAt)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Expires At</span>
                      <span className="text-sm text-gray-900">{formatTimestamp(registration.expiresAt)}</span>
                    </div>
                    {registration.isActive && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Time Remaining</span>
                        <span className="text-sm font-medium text-green-600">{formatDuration(registration.expiresIn)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Last Refresh</span>
                      <span className="text-sm text-gray-900">{formatTimestamp(registration.lastRefresh)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Refresh Count</span>
                      <span className="text-sm font-medium text-gray-900">{registration.refreshCount}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'technical' && (
              <div className="space-y-6">
                {/* SIP Transaction Details */}
                <div>
                  <button
                    onClick={() => setTechnicalExpanded(!technicalExpanded)}
                    className="flex items-center justify-between w-full text-left"
                  >
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">SIP Transaction</h3>
                    {technicalExpanded ? (
                      <ChevronUp className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                  {technicalExpanded && (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3 mt-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Call-ID</span>
                        <span className="text-sm font-mono text-gray-900 break-all">
                          {registration.callId || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">CSeq</span>
                        <span className="text-sm font-mono text-gray-900">
                          {registration.cseq ?? 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">User-Agent</span>
                        <span className="text-sm font-mono text-gray-900 break-all">
                          {registration.userAgent || 'N/A'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Association */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Association</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Registration ID</span>
                      <span className="text-sm font-mono text-gray-900">{registration.registrationId}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Peer ID</span>
                      <span className="text-sm font-mono text-gray-900">{registration.peerId || 'None'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Authenticated</span>
                      <span className={`text-sm font-medium ${registration.authenticated ? 'text-green-600' : 'text-gray-500'}`}>
                        {registration.authenticated ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Metadata */}
                {(() => {
                  const data = registration.registrationData;
                  if (typeof data !== 'object' || data === null) return null;
                  if (Object.keys(data).length === 0) return null;
                  return (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Additional Metadata</h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <pre className="text-xs font-mono text-gray-700 overflow-x-auto">
                          {JSON.stringify(data, null, 2)}
                        </pre>
                      </div>
                    </div>
                  );
                })()}

                {/* RFC 3261 Compliance Info */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">RFC 3261 Compliance</h3>
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                    <div className="flex items-start space-x-3">
                      <Shield className="h-5 w-5 text-blue-500 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Registration is RFC 3261 compliant</p>
                        <ul className="list-disc list-inside text-xs space-y-1 text-blue-700">
                          <li>Address of Record (AOR) follows SIP URI format</li>
                          <li>Transport: {registration.transport.toUpperCase()} {registration.transport === 'tls' && '(encrypted)'}</li>
                          <li>CSeq tracking enabled for replay prevention</li>
                          {registration.authenticated && <li>Digest authentication verified</li>}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onDelete}
              className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            >
              Delete Registration
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
